// PhysicianForm — add or edit a physician, with the NPPES registry doing the
// typing where it can: paste an NPI and the name, credentials, specialty and
// practice address arrive from the federal record; or search the registry by
// name and pick the right person from the list.
//
// Registry contact details are shown as suggestions beside what we hold.
// They never overwrite a number staff have used — that is the office we
// actually deal with, and NPPES is often a hospital's main campus.
import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import {
  savePhysician, getPhysicianOwnContact, listFacilities, nppesLookupNpi, nppesSearchName, isValidNpi, fmtPhone, digits, CONTACT_ROUTES,
} from '../../lib/directory';
import { Modal, ErrorBox, label, field, section, Pill, RefCode } from './ui';
import FacilityForm from './FacilityForm';

const blank = (seed = {}) => ({
  first_name: '', last_name: '', credentials: '', specialty: '', npi: '', facility_id: '',
  phone: '', fax: '', email: '', preferred_contact: '', notes: '', status: 'active', ...seed,
});

/** Split "Dr Sharon Beall" / "Beall, Sharon" / "beall" into first/last for a fresh record. */
const seedFromQuery = (q) => {
  let s = (q || '').replace(/\b(dr|doctor)\b\.?/gi, ' ').replace(/\b(md|do|np|pa)\b\.?/gi, ' ').replace(/\s+/g, ' ').trim();
  if (digits(s).length === 10 && isValidNpi(s)) return { npi: digits(s) };
  const comma = s.match(/^([^,]+),\s*(.+)$/);
  if (comma) return { last_name: comma[1].trim(), first_name: comma[2].trim() };
  const toks = s.split(' ').filter(Boolean);
  if (toks.length === 1) return { last_name: toks[0] };
  return { first_name: toks.slice(0, -1).join(' '), last_name: toks[toks.length - 1] };
};

export default function PhysicianForm({ physician, initialQuery, facilities: givenFacilities, onSaved, onClose, onVerify }) {
  const { profile } = useAuth();
  const actor = profile?.full_name || 'staff';
  const [form, setForm] = useState(() => physician ? blank(physician) : blank(seedFromQuery(initialQuery)));
  const [facilities, setFacilities] = useState(givenFacilities || []);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');
  const [nppes, setNppes] = useState(physician?.nppes_data ? unpack(physician.nppes_data) : null);
  const [candidates, setCandidates] = useState([]);
  const [newFacility, setNewFacility] = useState(false);
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  useEffect(() => { if (!givenFacilities) listFacilities().then(({ data }) => setFacilities(data || [])); }, [givenFacilities]);

  // `physician` is a directory row, whose phone, fax and email fall back to
  // the site's (and the fax to the parent organisation's). Edit the doctor's
  // own instead, and show what they inherit as the placeholder — otherwise
  // saving would copy, say, CHOA's records fax onto the doctor for good.
  const [ownLoaded, setOwnLoaded] = useState(!physician?.id);
  const [inherited, setInherited] = useState({});
  useEffect(() => {
    if (!physician?.id) return;
    getPhysicianOwnContact(physician.id).then(({ data, error }) => {
      if (error || !data) { setErr("Couldn't load this physician's own contact details. Close and try again."); return; }
      setForm(f => ({ ...f, phone: data.phone || '', fax: data.fax || '', email: data.email || '', preferred_contact: data.preferred_contact || '' }));
      setInherited({ phone: data.phone ? '' : physician.phone, fax: data.fax ? '' : physician.fax, email: data.email ? '' : physician.email });
      setOwnLoaded(true);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [physician?.id]);

  // A brand-new record seeded from a typed name: try the registry straight away.
  useEffect(() => {
    if (physician || !initialQuery) return;
    const seed = seedFromQuery(initialQuery);
    if (seed.npi) lookup(seed.npi);
    else if (seed.last_name) searchName(seed.last_name, seed.first_name);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyMatch = (m, { overwriteContact = false } = {}) => {
    setNppes(m);
    setForm(f => ({
      ...f,
      npi: m.npi,
      first_name: f.first_name || m.first_name,
      last_name: f.last_name || m.last_name,
      credentials: f.credentials || m.credentials || '',
      specialty: f.specialty || m.specialty || '',
      phone: overwriteContact ? (m.phone || f.phone) : (f.phone || m.phone || ''),
      fax:   overwriteContact ? (m.fax || f.fax)     : (f.fax || m.fax || ''),
      nppes_data: m.raw, nppes_checked_at: new Date().toISOString(),
    }));
    setCandidates([]);
  };

  const lookup = async (npiIn) => {
    const npi = digits(npiIn ?? form.npi);
    if (!isValidNpi(npi)) { setErr('That NPI fails the check digit — please re-check it.'); return; }
    setBusy('npi'); setErr('');
    try {
      const m = await nppesLookupNpi(npi);
      if (!m) setErr(`NPI ${npi} is not in the NPPES registry.`);
      else applyMatch(m);
    } catch (e) { setErr(e.message); }
    setBusy('');
  };

  const searchName = async (last = form.last_name, first = form.first_name) => {
    if (!last?.trim()) { setErr('Type at least a last name to search the registry.'); return; }
    setBusy('name'); setErr(''); setCandidates([]);
    try {
      let ms = await nppesSearchName(last.trim(), first?.trim(), 'GA');
      if (!ms.length) ms = await nppesSearchName(last.trim(), first?.trim(), null);
      if (!ms.length) setErr(`The registry has no individual named ${first ? first + ' ' : ''}${last}.`);
      else if (ms.length === 1) applyMatch(ms[0]);
      else setCandidates(ms);
    } catch (e) { setErr(e.message); }
    setBusy('');
  };

  const save = async () => {
    setBusy('save'); setErr('');
    const { data, error } = await savePhysician({ ...form, id: physician?.id }, actor);
    setBusy('');
    if (error) { setErr(error.message); return; }
    onSaved(data);
  };

  const differs = useMemo(() => nppes ? {
    phone: nppes.phone && nppes.phone !== digits(form.phone),
    fax:   nppes.fax && nppes.fax !== digits(form.fax),
  } : {}, [nppes, form.phone, form.fax]);

  const grid = (cols) => ({ display: 'grid', gridTemplateColumns: cols, gap: 12 });

  if (newFacility) {
    return (
      <FacilityForm facilities={facilities}
        onSaved={(fac) => { setFacilities(fs => [...fs, fac].sort((a, b) => a.name.localeCompare(b.name))); setForm(f => ({ ...f, facility_id: fac.id })); setNewFacility(false); }}
        onClose={() => setNewFacility(false)} />
    );
  }

  return (
    <Modal onClose={onClose} width={720}
      title={<>{physician ? <>Edit <RefCode code={physician.ref_code} /> {physician.last_name}, {physician.first_name}</> : 'Add physician'}</>}
      footer={<>
        {physician && onVerify && <button className="btn btn-outline" style={{ marginRight: 'auto', color: 'var(--success)', borderColor: 'var(--success-border)' }} onClick={() => onVerify(physician)}>✓ Details confirmed today</button>}
        <button className="btn btn-outline" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={save} disabled={busy === 'save' || !ownLoaded}>{busy === 'save' ? 'Saving…' : (physician ? 'Save changes' : 'Add to directory')}</button>
      </>}>
      <ErrorBox>{err}</ErrorBox>

      <div style={section}>Identity</div>
      <div style={grid('1fr auto')}>
        <div>
          <div style={label}>NPI</div>
          <input style={{ ...field, fontFamily: 'var(--mono)' }} value={form.npi ?? ''} onChange={set('npi')} placeholder="10 digits" inputMode="numeric"
            onBlur={() => { if (form.npi && !isValidNpi(form.npi)) setErr('That NPI fails the check digit — please re-check it.'); }} />
        </div>
        <div style={{ alignSelf: 'end' }}>
          <button className="btn btn-outline" onClick={() => lookup()} disabled={busy === 'npi' || !form.npi}>{busy === 'npi' ? 'Looking up…' : 'Look up in NPPES'}</button>
        </div>
      </div>
      <div style={{ ...grid('1fr 1fr 110px'), marginTop: 12 }}>
        <div><div style={label}>First name</div><input style={field} value={form.first_name} onChange={set('first_name')} /></div>
        <div><div style={label}>Last name</div><input style={field} value={form.last_name} onChange={set('last_name')} /></div>
        <div><div style={label}>Credentials</div><input style={field} value={form.credentials ?? ''} onChange={set('credentials')} placeholder="MD · NP · PA" /></div>
      </div>
      <div style={{ ...grid('1fr auto'), marginTop: 12 }}>
        <div><div style={label}>Specialty</div><input style={field} value={form.specialty ?? ''} onChange={set('specialty')} placeholder="Pediatrics · Neurology · …" /></div>
        <div style={{ alignSelf: 'end' }}>
          <button className="btn btn-outline" onClick={() => searchName()} disabled={busy === 'name' || !form.last_name}>{busy === 'name' ? 'Searching…' : 'Find NPI by name'}</button>
        </div>
      </div>

      {candidates.length > 0 && (
        <div style={{ marginTop: 12, border: '1px solid var(--accent-border)', borderRadius: 10, overflow: 'hidden' }}>
          <div style={{ padding: '8px 12px', background: 'var(--accent-bg)', fontSize: 12.5, color: 'var(--accent)', fontWeight: 600 }}>The registry has {candidates.length} people by that name — pick the right one</div>
          {candidates.map(m => (
            <div key={m.npi} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderTop: '1px solid var(--border)', fontSize: 13 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <b>{m.last_name}, {m.first_name}</b> {m.credentials || ''} <span style={{ color: 'var(--text2)' }}>· {m.specialty || '—'}</span>
                <div style={{ fontSize: 12, color: 'var(--text3)' }}>NPI {m.npi} · {[m.address_line1, m.city, m.state].filter(Boolean).join(', ')}{m.fax ? ` · fax ${fmtPhone(m.fax)}` : ''}</div>
              </div>
              <button className="btn btn-outline btn-sm" onClick={() => applyMatch(m)}>This one</button>
            </div>
          ))}
          <div style={{ padding: '6px 12px', fontSize: 12, color: 'var(--text3)', borderTop: '1px solid var(--border)' }}>None of these? Leave the NPI blank — the record still saves and shows under “Missing NPI”.</div>
        </div>
      )}

      {nppes && (
        <div style={{ marginTop: 12, background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 10, padding: '10px 12px', fontSize: 12.5 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <Pill tone="ok">NPPES ✓</Pill>
            <b>{nppes.last_name}, {nppes.first_name}</b> {nppes.credentials || ''} <span style={{ color: 'var(--text2)' }}>· {nppes.specialty || '—'}</span>
            {nppes.status && nppes.status !== 'A' && <Pill tone="bad">registry status {nppes.status}</Pill>}
          </div>
          <div style={{ color: 'var(--text3)' }}>Registry location: {[nppes.address_line1, nppes.address_line2, nppes.city, nppes.state, nppes.zip].filter(Boolean).join(', ') || '—'}
            {nppes.phone ? ` · tel ${fmtPhone(nppes.phone)}` : ''}{nppes.fax ? ` · fax ${fmtPhone(nppes.fax)}` : ''}</div>
          {(differs.phone || differs.fax) && (
            <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', color: 'var(--text2)' }}>
              The registry's {[differs.phone && 'phone', differs.fax && 'fax'].filter(Boolean).join(' and ')} differ{differs.phone && differs.fax ? '' : 's'} from ours. Ours is the office we deal with;
              <button className="btn btn-outline btn-sm" onClick={() => applyMatch(nppes, { overwriteContact: true })}>use the registry's instead</button>
            </div>
          )}
        </div>
      )}

      <div style={section}>Where they work</div>
      <div>
        <div style={label}>Facility / practice</div>
        <select style={field} value={form.facility_id || ''} onChange={e => { if (e.target.value === '__new__') setNewFacility(true); else set('facility_id')(e); }}>
          <option value="">— none / unknown —</option>
          <option value="__new__">＋ Add a practice that isn't listed…</option>
          {facilities.filter(f => f.status !== 'inactive' || f.id === form.facility_id).map(f => (
            <option key={f.id} value={f.id}>{f.ref_code} · {f.name}{f.city ? ` (${f.city})` : ''}</option>
          ))}
        </select>
        <div style={{ fontSize: 11.5, color: 'var(--text3)', marginTop: 4 }}>
          Address comes from the facility. Phone and fax below override the facility's when set.
          {nppes && <> The registry's location is often an old or main-campus address — pick the office you actually deal with.</>}
        </div>
      </div>

      <div style={section}>Contact</div>
      <div style={grid('1fr 1fr 1fr')}>
        <div><div style={label}>Phone</div><input style={field} value={form.phone ?? ''} onChange={set('phone')} placeholder={inherited.phone ? `↑ ${fmtPhone(inherited.phone)}` : '(404) 555-0100'} inputMode="tel" /></div>
        <div><div style={label}>Fax</div><input style={field} value={form.fax ?? ''} onChange={set('fax')} placeholder={inherited.fax ? `↑ ${fmtPhone(inherited.fax)}` : '(404) 555-0101'} inputMode="tel" /></div>
        <div><div style={label}>Email</div><input style={field} value={form.email ?? ''} onChange={set('email')} placeholder={inherited.email ? `↑ ${inherited.email}` : ''} inputMode="email" /></div>
      </div>
      <div style={{ ...grid('180px 1fr'), marginTop: 12 }}>
        <div>
          <div style={label}>Best route</div>
          <select style={field} value={form.preferred_contact || ''} onChange={set('preferred_contact')}>
            {CONTACT_ROUTES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
        </div>
        <div><div style={label}>Notes</div><input style={field} value={form.notes || ''} onChange={set('notes')} placeholder="Office closes 12:00 Fri. Ask for Renee." /></div>
      </div>
      {physician && (
        <div style={{ ...grid('180px 1fr'), marginTop: 12 }}>
          <div>
            <div style={label}>Status</div>
            <select style={field} value={form.status} onChange={set('status')}>
              <option value="active">Active</option><option value="inactive">Inactive (retired / left)</option>
            </select>
          </div>
        </div>
      )}
    </Modal>
  );
}

// nppes_data stores the registry's raw record; rebuild the flat shape the form uses.
function unpack(raw) {
  if (!raw || !raw.basic) return null;
  const b = raw.basic; const loc = (raw.addresses || []).find(a => a.address_purpose === 'LOCATION') || (raw.addresses || [])[0] || {};
  const tax = (raw.taxonomies || []).find(t => t.primary) || (raw.taxonomies || [])[0] || {};
  const tc = (s) => (s || '').toLowerCase().replace(/(^|[\s\-'])([a-z])/g, (m, p, c) => p + c.toUpperCase());
  return { npi: String(raw.number), first_name: tc(b.first_name), last_name: tc(b.last_name), credentials: (b.credential || '').replace(/\./g, '').toUpperCase() || null,
    specialty: tax.desc || null, address_line1: tc(loc.address_1), address_line2: tc(loc.address_2), city: tc(loc.city), state: loc.state, zip: (loc.postal_code || '').slice(0, 5),
    phone: digits(loc.telephone_number).slice(-10) || null, fax: digits(loc.fax_number).slice(-10) || null, status: b.status, raw };
}
